import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { allMovies, articles, channels } from '@/mocks/movies';
import type { MovieItem, ArticleItem } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const movie = allMovies.find((m) => m.id === id);
  const article = articles.find((a) => a.id === id);
  const content = movie || article;

  if (!content) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-file-unknow-line text-6xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">内容未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的内容不存在或已被移除</p>
            <Link to="/" className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const isArticle = 'summary' in content && 'author' in content;
  const articleContent = isArticle ? (content as ArticleItem) : null;
  const movieContent = !isArticle ? (content as MovieItem) : null;

  const articleSchema = isArticle && articleContent ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleContent.title,
    description: articleContent.summary,
    author: { '@type': 'Person', name: articleContent.author },
    datePublished: articleContent.publishTime,
    image: articleContent.imageUrl,
  } : null;

  const movieSchema = !isArticle && movieContent ? {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movieContent.title,
    description: movieContent.description,
    image: movieContent.imageUrl,
    dateCreated: movieContent.year.toString(),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movieContent.rating.toString(),
      bestRating: '10',
    },
  } : null;

  const channelData = channels.find((ch) => ch.name === content.channel);
  const channelSlug = channelData ? channelData.slug : '';

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: content.channel, href: `/category/${channelSlug}` },
    { label: content.title },
  ];

  return (
    <>
      <SEOHead
        title={content.title}
        description={isArticle && articleContent ? articleContent.summary : (movieContent ? movieContent.description : '')}
        keywords={`${content.title},${content.channel},麻花影视,在线观看`}
        canonicalUrl={`/article/${id}`}
        ogType="article"
        ogImage={content.imageUrl}
        schema={articleSchema || movieSchema}
        articleData={isArticle && articleContent ? {
          publishedTime: articleContent.publishTime,
          author: articleContent.author,
        } : undefined}
      />

      <Navbar />

      <main className="pt-16">
        <article className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {movieContent && (
              <div className="relative aspect-video rounded-xl overflow-hidden mb-8">
                <img
                  src={movieContent.imageUrl}
                  alt={movieContent.title}
                  title={`${movieContent.title} - ${movieContent.channel}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1.5 bg-primary-500 text-background-50 text-lg font-bold rounded-lg">
                      {movieContent.rating}
                    </span>
                    <span className="text-white font-semibold text-lg">评分</span>
                  </div>
                </div>
              </div>
            )}

            {articleContent && (
              <div className="relative aspect-video rounded-xl overflow-hidden mb-8">
                <img
                  src={articleContent.imageUrl}
                  alt={articleContent.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                {content.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                {content.title}
              </h1>

              {movieContent && (
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {movieContent.year}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> {movieContent.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {movieContent.views.toLocaleString()} 次播放
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-tv-2-line"></i> {movieContent.channel}
                  </span>
                </div>
              )}

              {articleContent && (
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {articleContent.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {articleContent.publishTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {articleContent.views.toLocaleString()} 阅读
                  </span>
                </div>
              )}
            </header>

            <div className="prose-custom">
              {movieContent && (
                <div className="bg-background-100 rounded-xl p-5 md:p-6 mb-8">
                  <h2 className="text-lg font-semibold text-foreground-900 mb-3">影片简介</h2>
                  <p className="text-foreground-700 leading-relaxed text-sm md:text-base">{movieContent.description}</p>
                </div>
              )}

              {articleContent && (
                <div
                  className="text-foreground-800 leading-relaxed text-sm md:text-base space-y-4"
                  dangerouslySetInnerHTML={{ __html: articleContent.content }}
                />
              )}
            </div>

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to={`/category/${channelSlug}`}
                  className="flex items-center gap-2 text-primary-500 hover:text-primary-600 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i> 返回{content.channel}
                </Link>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-foreground-400">分享：</span>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors whitespace-nowrap" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors whitespace-nowrap" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors whitespace-nowrap" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}